/*
 * Floretta delivery map (sections/floretta-delivery-map.liquid): the interactions only; the map
 * itself is a fixed SVG drawn by the section.
 * - Hovering an area or its row with a mouse highlights both; a click or tap selects it, and it
 *   stays selected until another area is picked.
 * - When the customer checks a postal code (floretta-delivery-check.js), highlights their area.
 * - On a city delivery page, data-selected names the page's area, which starts highlighted; a
 *   postal code saved from an earlier visit then only marks its row as theirs.
 */
(() => {
  if (customElements.get("delivery-map")) return;

  const CODE_KEY = "floretta:delivery-postal-code";

  class DeliveryMap extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.zones = new Map();
      for (const item of this.querySelectorAll(".delivery-map__zone")) {
        const zone = item.dataset.zone;
        const shape = this.querySelector(`.delivery-map__shape[data-zone="${zone}"]`);
        if (!shape) continue;
        this.zones.set(zone, {
          item,
          shape,
          label: this.querySelector(`.delivery-map__label[data-zone="${zone}"]`),
          postalCodes: shape.dataset.postalCodes.split(", ")
        });

        // Hover is for mice only. On touch screens the "leave" after lifting the finger would undo
        // the tap, and iOS skips the click when hovering changes the page.
        for (const target of [shape, item]) {
          target.addEventListener("pointerenter", (event) => {
            if (event.pointerType === "mouse") this.highlight(zone);
          });
          target.addEventListener("pointerleave", (event) => {
            if (event.pointerType === "mouse") this.highlight(this.selected);
          });
        }
        shape.addEventListener("click", () => this.select(zone));
        item.querySelector("button").addEventListener("click", () => this.select(zone));
      }

      if (this.zones.has(this.dataset.selected)) this.select(this.dataset.selected);

      let stored = "";
      try {
        stored = localStorage.getItem(CODE_KEY) || "";
      } catch {}
      this.showPostalCode(stored, !this.selected);
      this.onChecked = (event) => this.showPostalCode(event.detail.code);
      document.addEventListener("floretta:delivery-checked", this.onChecked);
    }

    disconnectedCallback() {
      document.removeEventListener("floretta:delivery-checked", this.onChecked);
    }

    highlight(zone) {
      for (const [key, { item, shape, label }] of this.zones) {
        const active = key === zone;
        item.classList.toggle("is-active", active);
        shape.classList.toggle("is-active", active);
        label?.classList.toggle("is-active", active);
        // SVG has no z-index: draw the active area last so its outline isn't covered.
        if (active) shape.parentNode.append(shape);
      }
    }

    select(zone) {
      this.selected = zone;
      this.highlight(zone);
    }

    showPostalCode(code, highlight = true) {
      const prefix = String(code).toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 3);
      let home = null;
      for (const [zone, { item, postalCodes }] of this.zones) {
        const match = postalCodes.includes(prefix);
        item.classList.toggle("is-home", match);
        if (match) home = zone;
      }
      if (home && highlight) this.select(home);
    }
  }

  customElements.define("delivery-map", DeliveryMap);
})();
